import {AxiosRequestConfig} from 'axios'
import {v1 as uuid} from 'uuid'
import {createHttpInstance} from '../coreApi/createHttpInstance'
import {transformRequests} from '../coreApi/transformRequests'
import {transformResponse} from '../coreApi/transformResponse'
import {NotAuthorizedError} from '../../customErrors'
import {API_URL} from '../../constants'
import {sessionManager} from '../../utils'
// The RAW transport call, not the domain-layer `api.auth.refresh`. Reaching for
// the domain layer from here would be an import cycle (it imports this module for
// the shared axios instance), which `import/no-cycle` rejects — and rightly: the
// barrel would capture `refresh` into its object literal while this module was
// still initializing and leave it undefined forever. This module IS the transport
// layer, so calling coreApi directly is the layer-appropriate move; the never
// throws contract the interceptor depends on is honoured by the try/catch in
// `attemptSessionRecovery` below. Application code still goes through
// `api.auth.refresh` (see app/SessionRefresher.tsx).
import {refresh as refreshLegacySession} from '../coreApi/auth/refresh'

/*
   Making a special point to use the same history object for the Router instead of browser api.
   I belive this serves as a bit of a polyfill and should avoid hard to debug scenarios where stuff works in most browsers,
   but user's are properly being redirected on some
*/
// import {history} from '../../app/history'

if (!API_URL) {
  throw new Error('No API URL Environment Variable')
}

const http = createHttpInstance({baseURL: API_URL})

/**
 * Attempts a silent Auth0 re-exchange. Registered by `SessionRefresher` because
 * the exchange needs the React auth store (Auth0's hooks), which a module-level
 * interceptor cannot reach. Resolves true when the session was recovered.
 */
type SilentSessionRecovery = () => Promise<boolean>

let silentSessionRecovery: SilentSessionRecovery | undefined

/**
 * Hands the transport layer a way to re-exchange the Auth0 token silently, and
 * returns the matching unregister function for the caller's effect cleanup.
 */
export const registerSilentSessionRecovery = (
  recover: SilentSessionRecovery,
): (() => void) => {
  silentSessionRecovery = recover

  return (): void => {
    if (silentSessionRecovery === recover) {
      silentSessionRecovery = undefined
    }
  }
}

// Requests that ARE the recovery. A 401 from one of these must never start
// another recovery: the refresh is its first step and the exchange its fallback,
// so re-entering here would either recurse or join — and therefore await — the
// very in-flight recovery that is waiting on this request. Logout is here for a
// different reason: a session that is being ended deliberately must not be
// resurrected behind the user's back.
const SESSION_REFRESH_PATH = '/v1/auth/refresh'
const TOKEN_EXCHANGE_PATH = '/v1/auth/exchange'
const LOGOUT_PATH = '/v1/auth/logout'

const NO_RECOVERY_PATHS = [
  SESSION_REFRESH_PATH,
  TOKEN_EXCHANGE_PATH,
  LOGOUT_PATH,
]

// The two calls the recovery makes itself. Their 401 is an *input* to the
// recovery decision, so the interceptor must not expire the session on it —
// a dead session row is exactly the case the Auth0 re-exchange fallback exists
// to recover from, and expiring here would kill the session first. When the
// recovery genuinely fails, the orchestrator below expires it once.
const RECOVERY_OWNED_PATHS = [SESSION_REFRESH_PATH, TOKEN_EXCHANGE_PATH]

// Requests the app makes to maintain the session, rather than because the user
// did something. They must NOT count as activity for the idle-session timer.
//
// The scheduled /v1/auth/refresh (app/SessionRefresher.tsx) fires every 15
// minutes, which is INSIDE the 30-minute idle window in app/Router.tsx. When it
// reset the timer, the app kept marking itself as used before the window could
// ever close, so a completely idle tab was never logged out — it held both its
// own session and the server's session row open indefinitely. That silently
// removed the idle logout entirely (AB#24880).
//
// The design intent is that ACTIVITY keeps a session alive: a real request the
// user caused, local interaction (hooks/useSessionActivity.ts), or an upload in
// flight (components/PhotoSelector/PhotoSelector.tsx). A timer talking to the
// auth service on its own is none of those.
const SESSION_MAINTENANCE_PATHS = [SESSION_REFRESH_PATH, TOKEN_EXCHANGE_PATH]

const matchesPath = (url: string | undefined, paths: string[]): boolean =>
  Boolean(url) && paths.some((path) => (url as string).includes(path))

// Marks a config that has already been retried after a recovery, so
// "recover once, retry once" cannot become a loop.
const SESSION_RETRY_FLAG = '__campanionSessionRetried'

type RetryableRequestConfig = AxiosRequestConfig & {
  [SESSION_RETRY_FLAG]?: boolean
}

// Coalesces every 401 that arrives while a recovery is running onto the single
// in-flight attempt. Concurrent recoveries would each mint their own tokens and
// race Auth0's refresh-token rotation — the same failure TokenService's
// `inFlight` guard removes at the exchange layer (AB#19499).
let recoveryInFlight: Promise<boolean> | null = null

// The expiry is a hard page reload to /login?expired=1, so it must happen at
// most once no matter how many requests failed. Cleared again by a successful
// recovery: the session is demonstrably alive, so a later lapse deserves its own
// expiry.
let hasExpiredSession = false

const attemptSessionRecovery = async (): Promise<boolean> => {
  // Cheapest first: rotate the legacy session row. Everything here is caught —
  // a throw escaping into the interceptor would re-enter the very error path it
  // is recovering from.
  try {
    await refreshLegacySession(http)

    // Success is the HTTP status, not a body field: the endpoint answers
    // `{"Token":""}` and puts the rotated TokenID in a Secure/HttpOnly cookie.
    // `validateStatus` is left at its default here, so a non-2xx rejects into
    // the catch below and reaching this line means the row rotated.
    //
    // This used to require `data.Status === 'Success'`, a field the auth
    // contract does not define, so this branch was unreachable and every 401
    // recovery skipped the cheap rotation for the Auth0 re-exchange (AB#24880).
    return true
  } catch {
    // A dead session row 401s here. That is not a failure to handle, it is the
    // answer: rotation cannot help, so fall through to the re-exchange.
  }

  // The row is gone, so only the Auth0 bearer can vouch for the user now.
  if (!silentSessionRecovery) {
    return false
  }

  try {
    return await silentSessionRecovery()
  } catch {
    return false
  }
}

const recoverSession = (): Promise<boolean> => {
  if (!recoveryInFlight) {
    recoveryInFlight = attemptSessionRecovery()
      .then((recovered) => {
        if (recovered) {
          hasExpiredSession = false
        }
        return recovered
      })
      .finally(() => {
        recoveryInFlight = null
      })
  }

  return recoveryInFlight
}

const expireSessionOnce = (): void => {
  if (hasExpiredSession) {
    return
  }
  hasExpiredSession = true
  sessionManager.immediateExpire()
}

transformRequests(http, (request) => {
  if (!request.headers) throw new Error('request.headers is undefined.')

  request.headers['X-Request-ID'] = uuid()

  return request
})

transformResponse(
  http,
  (response) => {
    // Couldn't get revoke to work after 5 minute delay, so I used this to trigger the same effect as a 401 when fetching the microposts
    // if (response.config.url?.indexOf('microposts') !== -1) {
    //   const location = {
    //     pathname: '/',
    //     state: {httpStatus: 401},
    //   }
    //   history.push(location)

    //   sessionStorage.clear()
    // }
    // Active use resets sessionTimer — but only actual use. A session
    // maintenance call is the app talking to itself (AB#24880).
    if (!matchesPath(response.config?.url, SESSION_MAINTENANCE_PATHS)) {
      sessionManager.reset()
    }
    return response
  },
  (error) => {
    if (!error.response || error.response.status !== 401) {
      return Promise.reject(error)
    }

    const config = error.config as RetryableRequestConfig | undefined

    // A 401 used to expire the session and hard-reload the page immediately, so
    // a token that lapsed for a moment cost the user whatever they were doing —
    // most expensively, an in-progress photo upload. Recover silently first and
    // only give up if that fails (AB#24880). One view already worked this around
    // locally (CampanionStaffAccessView, AB#19213); doing it here fixes every
    // caller at once.
    const isTerminal =
      !config ||
      config[SESSION_RETRY_FLAG] ||
      matchesPath(config.url, NO_RECOVERY_PATHS)

    if (isTerminal) {
      if (!matchesPath(config?.url, RECOVERY_OWNED_PATHS)) {
        expireSessionOnce()
      }
      return Promise.reject(new NotAuthorizedError('Not authorized'))
    }

    return recoverSession().then((recovered) => {
      if (!recovered) {
        expireSessionOnce()
        return Promise.reject(new NotAuthorizedError('Not authorized'))
      }

      // Retry exactly once, with the fresh cookie the recovery just installed.
      const retryConfig: RetryableRequestConfig = {
        ...config,
        [SESSION_RETRY_FLAG]: true,
      }

      return http.request(retryConfig)
    })
  },
)

export {http}
